/**
 * RequestorSlots — the two requestor slots on one site or well (OPEN-053).
 *
 * Dropped into both `TankBatteryDetail` and `WellDetail`; the only difference between them is
 * `targetType`. Read-only for a plain dispatcher, who still needs to see who is on a site.
 *
 * Every write goes through `slot-edit.ts`, so the confirm-before-displacing rule holds here
 * exactly as it does in the bulk editor — see that file for why a *replace* is confirmed even
 * though the API only gates an explicit clear.
 *
 * Deactivated people are still SHOWN when they hold a slot (with a badge) rather than silently
 * vanishing: a site whose requestor left needs to look wrong, not look empty.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { useRequestors, useAssignments, useSetAssignments, type AssignedRequestor } from './hooks';
import type { AssignmentTargetType } from './hooks';
import { confirmationFor, isNoop, slotBody, type SlotChange, type SlotNumber } from './slot-edit';
import { ConfirmDialog } from './ConfirmDialog';
import { Pill, subText } from '../sites-operators/ui-bits';

const sectionTitle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 700,
  color: 'var(--color-text-tertiary)',
  margin: '18px 0 8px',
};

const selectStyle: React.CSSProperties = {
  minHeight: '36px',
  padding: '6px 8px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  minWidth: '220px',
};

export interface RequestorSlotsProps {
  readonly targetType: AssignmentTargetType;
  readonly targetId: string;
  readonly canManage: boolean;
}

export function RequestorSlots({
  targetType,
  targetId,
  canManage,
}: RequestorSlotsProps): React.JSX.Element {
  const { data, isLoading, isError } = useAssignments(targetType, targetId);
  const { data: listData } = useRequestors(true);
  const setAssignments = useSetAssignments();
  const [pending, setPending] = React.useState<SlotChange | null>(null);

  const all = listData?.requestors ?? [];
  const occupantOf = (slot: SlotNumber): AssignedRequestor | null =>
    (slot === 1 ? data?.slot1 : data?.slot2) ?? null;

  const commit = (change: SlotChange): void => {
    setAssignments.mutate(slotBody(targetType, targetId, change), {
      onSuccess: () => {
        toast.success(change.kind === 'clear' ? 'Requestor removed' : 'Requestor assigned');
        setPending(null);
      },
      onError: () => {
        toast.error('Could not save the assignment');
        setPending(null);
      },
    });
  };

  /** Ask first if anyone is displaced; otherwise go straight through. */
  const request = (change: SlotChange): void => {
    const occupant = occupantOf(change.slot);
    if (isNoop(change, occupant)) return;
    const nextName =
      change.kind === 'set' ? (all.find((r) => r.id === change.requestorId)?.name ?? null) : null;
    if (confirmationFor(change, occupant, nextName) === null) {
      commit(change);
      return;
    }
    setPending(change);
  };

  const pendingPrompt =
    pending === null
      ? null
      : confirmationFor(
          pending,
          occupantOf(pending.slot),
          pending.kind === 'set'
            ? (all.find((r) => r.id === pending.requestorId)?.name ?? null)
            : null,
        );

  const renderSlot = (slot: SlotNumber): React.JSX.Element => {
    const occupant = occupantOf(slot);
    return (
      <div
        key={slot}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 0',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ ...subText, minWidth: '90px' }}>Requestor {slot}</span>
        {canManage ? (
          <select
            aria-label={`Requestor ${slot}`}
            value={occupant?.id ?? ''}
            disabled={setAssignments.isPending}
            onChange={(e): void => {
              const v = e.target.value;
              request(v === '' ? { kind: 'clear', slot } : { kind: 'set', slot, requestorId: v });
            }}
            style={selectStyle}
          >
            <option value="">— None —</option>
            {/* A deactivated occupant is not in the active list, so keep it as an option or
                the select would silently render as "None" over a real assignment. */}
            {occupant !== null && !occupant.isActive && (
              <option value={occupant.id}>{occupant.name} (inactive)</option>
            )}
            {all.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.company !== null ? ` — ${r.company}` : ''}
              </option>
            ))}
          </select>
        ) : (
          <span style={{ fontSize: '14px' }}>{occupant?.name ?? '—'}</span>
        )}
        {occupant !== null && !occupant.isActive && <Pill label="Inactive" tone="muted" />}
        {occupant !== null && (
          <span style={subText}>
            {[occupant.company, occupant.phone, occupant.email]
              .filter((v) => v !== null)
              .join(' · ')}
          </span>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={sectionTitle}>Requestors</div>
      {isLoading && <p style={subText}>Loading…</p>}
      {isError && (
        <p style={{ ...subText, color: 'var(--color-danger)' }}>Could not load requestors.</p>
      )}
      {!isLoading && !isError && (
        <>
          {renderSlot(1)}
          {renderSlot(2)}
          <p style={subText}>
            Dispatchers pick one of these when they create a ticket for this location.
          </p>
        </>
      )}

      <ConfirmDialog
        prompt={pendingPrompt}
        pending={setAssignments.isPending}
        onCancel={(): void => setPending(null)}
        onConfirm={(): void => {
          if (pending !== null) commit(pending);
        }}
      />
    </div>
  );
}
